import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import DateField from './DateField';
import additionalChargesService, { ChargeRate } from '../../services/additionalcharges.service';

/**
 * Effective-dated rates sub-editor for a Transportation Addl Charge
 * (quote.additionalchargerates). Mirrors the fsc_tariff lines editor: rows are
 * effective date (DateTimePicker — numeric shortcuts) + minimum/default rate +
 * delete. V1 rule: an effective date is required when a rate is set. Removed rows
 * are soft-deleted on save.
 */
interface Props {
  chargeRecno: number;
  onClose: () => void;
  onSaved: () => void;
}

const s = (v: any): string => (v == null ? '' : String(v));
let _tmpId = -1;
const tmpId = () => _tmpId--;

const AdditionalChargesRatesDrawer: React.FC<Props> = ({ chargeRecno, onClose, onSaved }) => {
  const [rates, setRates] = useState<ChargeRate[]>([]);
  const [deletes, setDeletes] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    additionalChargesService.getRates(chargeRecno, ctrl.signal)
      .then((r) => setRates(r.rates))
      .catch(() => {});
    return () => ctrl.abort();
  }, [chargeRecno]);

  const setRate = (recno: number, patch: Partial<ChargeRate>) =>
    setRates((p) => p.map((r) => (r.recno === recno ? { ...r, ...patch } : r)));
  const addRate = () =>
    setRates((p) => [...p, { recno: tmpId(), effectivedate: '', minimumrate: '', defaultrate: '' }]);
  const removeRate = (rate: ChargeRate) => {
    if (rate.recno > 0) setDeletes((d) => [...d, rate.recno]);
    setRates((p) => p.filter((r) => r.recno !== rate.recno));
  };

  // A row "has a rate" if either rate is > 0; such a row requires an effective date.
  const hasRate = (r: ChargeRate) => parseFloat(s(r.minimumrate)) > 0 || parseFloat(s(r.defaultrate)) > 0;
  const hasDate = (r: ChargeRate) => s(r.effectivedate).trim() !== '';

  const errors = useMemo(
    () =>
      rates.some((r) => hasRate(r) && !hasDate(r))
        ? ['Effective Date is required when a rate is set.']
        : [],
    [rates],
  );

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      const payload = rates
        .filter((r) => hasRate(r) || hasDate(r)) // drop fully-blank trailing rows
        .map((r) => ({
          recno: r.recno > 0 ? r.recno : 0,
          effectivedate: s(r.effectivedate),
          minimumrate: s(r.minimumrate),
          defaultrate: s(r.defaultrate),
        }));
      await additionalChargesService.saveRates(chargeRecno, { deletes, rates: payload });
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save rates.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, rates, deletes, chargeRecno, onClose, onSaved]);

  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];
  const numCls =
    'w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm text-right';

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> Save Rates</>)}
      </button>
    </div>
  );

  return (
    <Drawer isOpen onClose={onClose} title="Add / Edit Rates" position="right" width="38rem" footer={footer}>
      <div className="space-y-4">
        <div ref={topRef} />
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-[1.3fr_1fr_1fr_auto] gap-2 items-center text-xs font-semibold text-muted px-1">
          <div>Effective Date</div>
          <div className="text-right">Minimum Rate</div>
          <div className="text-right">Default Rate</div>
          <div className="w-8 text-center">Del</div>
        </div>

        {rates.map((r) => (
          <div key={r.recno} className="grid grid-cols-[1.3fr_1fr_1fr_auto] gap-2 items-center">
            <DateField value={s(r.effectivedate)} disabled={isSaving}
              invalid={showErrors && hasRate(r) && !hasDate(r)}
              onChange={(v) => setRate(r.recno, { effectivedate: v })} />
            <input type="number" step="0.00001" disabled={isSaving} className={numCls}
              value={s(r.minimumrate)} onChange={(e) => setRate(r.recno, { minimumrate: e.target.value })} />
            <input type="number" step="0.00001" disabled={isSaving} className={numCls}
              value={s(r.defaultrate)} onChange={(e) => setRate(r.recno, { defaultrate: e.target.value })} />
            <div className="w-8 flex justify-center">
              <button type="button" onClick={() => removeRate(r)} disabled={isSaving}
                className="p-1 text-red-500 hover:text-red-700" title="Remove rate">
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        <button type="button" onClick={addRate} disabled={isSaving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
          <PlusIcon className="w-4 h-4" /> Add Rate
        </button>
      </div>
    </Drawer>
  );
};

export default AdditionalChargesRatesDrawer;
